import { Fragment, useCallback, useMemo, type FC } from 'react'
import {
  Box,
  Checkbox,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { DeleteOutlined, HolderOutlined } from '@ant-design/icons'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Paper from 'components/@extended/Paper'
import { TextField } from 'components/@extended/TextField'
import type { CategoryData, RewardData } from '../types'

interface ReviewTableProps {
  mode: 'categories' | 'rewards'
  categories: CategoryData[]
  rewards: RewardData[]
  onCategoryEdit: (id: string, field: keyof CategoryData, value: string | number) => void
  onRewardEdit: (id: string, field: keyof RewardData, value: string | number | boolean) => void
  onCategoryRemove: (id: string) => void
  onRewardRemove: (id: string) => void
  onCategoryReorder: (categories: CategoryData[]) => void
  onRewardsReorder: (rewards: RewardData[]) => void
  variant?: 'review' | 'approved'
  footer?: React.ReactNode
  maxWidth?: number
}

interface SortableCategoryRowProps {
  category: CategoryData
  onCategoryEdit: (id: string, field: keyof CategoryData, value: string | number) => void
  onCategoryRemove: (id: string) => void
  isDraggable: boolean
}

const SortableCategoryRow: FC<SortableCategoryRowProps> = ({
  category,
  onCategoryEdit,
  onCategoryRemove,
  isDraggable,
}) => {
  const categoryId = category.id || category.tempId || ''
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: categoryId,
    disabled: !isDraggable,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  }

  return (
    <TableRow ref={setNodeRef} style={style} hover {...attributes}>
      <TableCell>
        <TextField
          value={category.name}
          onChange={(event) => onCategoryEdit(categoryId, 'name', event.target.value)}
          variant="outlined"
          fullWidth
        />
      </TableCell>
      <TableCell align="center">
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
          <Tooltip title={isDraggable ? 'Drag to reorder' : 'Primary category cannot be reordered'}>
            <span>
              <IconButton size="small" color="primary" {...(isDraggable ? listeners : {})} disabled={!isDraggable}>
                <HolderOutlined />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            {category.displayOrder}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="center">
        <Tooltip title="Delete category">
          <IconButton size="small" color="error" onClick={() => onCategoryRemove(categoryId)}>
            <DeleteOutlined />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  )
}

interface RewardGroup {
  id: string
  name: string
  displayOrder: number
  rewards: RewardData[]
}

interface SortableRewardRowProps {
  reward: RewardData
  onRewardEdit: (id: string, field: keyof RewardData, value: string | number | boolean) => void
  onRewardRemove: (id: string) => void
  isDraggable: boolean
  variant: 'review' | 'approved'
}

const SortableRewardRow: FC<SortableRewardRowProps> = ({
  reward,
  onRewardEdit,
  onRewardRemove,
  isDraggable,
  variant,
}) => {
  const rewardId = reward.id || reward.tempId || ''
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rewardId,
    disabled: !isDraggable,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  }

  return (
    <TableRow ref={setNodeRef} style={style} hover {...attributes}>
      <TableCell>
        {variant === 'review' ? (
          <TextField
            value={reward.name}
            onChange={(event) => onRewardEdit(rewardId, 'name', event.target.value)}
            variant="outlined"
            fullWidth
          />
        ) : (
          <Typography variant="body2">{reward.name}</Typography>
        )}
      </TableCell>
      <TableCell align="center">
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
          {variant === 'review' && (
            <Tooltip title={isDraggable ? 'Drag to reorder' : 'Drag to reorder'}>
              <span>
                <IconButton size="small" color="primary" {...listeners} disabled={!isDraggable}>
                  <HolderOutlined />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Typography variant="body2" color="text.secondary">
            {reward.displayOrder}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="center">
        <Checkbox
          checked={Boolean(reward.supportsEmployeeContribution)}
          onChange={(event) =>
            variant === 'review' && onRewardEdit(rewardId, 'supportsEmployeeContribution', event.target.checked)
          }
          disabled={variant !== 'review'}
          size="small"
        />
      </TableCell>
      {variant === 'review' && (
        <TableCell align="center">
          <Tooltip title="Delete reward">
            <IconButton size="small" color="error" onClick={() => onRewardRemove(rewardId)}>
              <DeleteOutlined />
            </IconButton>
          </Tooltip>
        </TableCell>
      )}
    </TableRow>
  )
}

const ReviewTable: FC<ReviewTableProps> = ({
  mode,
  categories,
  rewards,
  onCategoryEdit,
  onRewardEdit,
  onCategoryRemove,
  onRewardRemove,
  onCategoryReorder,
  onRewardsReorder,
  variant = 'review',
  footer,
  maxWidth = 960,
}) => {
  const isReview = variant === 'review'
  const sortedCategories = useMemo(() => [...categories].sort((a, b) => a.displayOrder - b.displayOrder), [categories])

  const rewardGroups = useMemo<RewardGroup[]>(() => {
    const categoriesById = new Map<string, CategoryData>()
    const categoriesByName = new Map<string, CategoryData>()

    categories.forEach((category) => {
      const categoryId = category.id || category.tempId || category.name
      categoriesById.set(categoryId, category)
      categoriesByName.set(category.name.toLowerCase(), category)
    })

    const groups = new Map<string, RewardGroup>()

    rewards.forEach((reward) => {
      const lookupCategory = reward.categoryId
        ? categoriesById.get(reward.categoryId)
        : reward.categoryName
          ? categoriesByName.get(reward.categoryName.toLowerCase())
          : undefined
      const categoryKey = lookupCategory?.id ?? lookupCategory?.tempId
      const groupId = categoryKey || reward.categoryId || reward.categoryName || 'unassigned'
      const groupName = lookupCategory?.name ?? reward.categoryName ?? 'Unassigned'
      const groupOrder = lookupCategory?.displayOrder ?? Number.POSITIVE_INFINITY

      if (!groups.has(groupId)) {
        groups.set(groupId, {
          id: groupId,
          name: groupName,
          displayOrder: groupOrder,
          rewards: [],
        })
      }
      groups.get(groupId)!.rewards.push(reward)
    })

    const grouped = Array.from(groups.values())
    grouped.forEach((group) => {
      group.rewards = [...group.rewards].sort((a, b) => a.displayOrder - b.displayOrder)
    })

    grouped.sort((a, b) => {
      if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder
      return a.name.localeCompare(b.name)
    })

    return grouped
  }, [categories, rewards])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleCategoryDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id) {
        return
      }

      const oldIndex = sortedCategories.findIndex((cat) => (cat.id || cat.tempId) === active.id)
      const newIndex = sortedCategories.findIndex((cat) => (cat.id || cat.tempId) === over.id)

      if (oldIndex === -1 || newIndex === -1) {
        return
      }

      if (sortedCategories[oldIndex].displayOrder === 0) {
        return
      }

      if (newIndex === 0 && sortedCategories[0].displayOrder === 0) {
        return
      }

      const reordered = arrayMove(sortedCategories, oldIndex, newIndex)
      const hasPrimaryCategory = reordered.some((category) => category.displayOrder === 0)
      let nextOrder = 1

      const nextCategories = reordered.map((category) => {
        if (hasPrimaryCategory && category.displayOrder === 0) {
          return { ...category, displayOrder: 0 }
        }

        const updated = { ...category, displayOrder: nextOrder }
        nextOrder += 1
        return updated
      })

      onCategoryReorder(nextCategories)
    },
    [sortedCategories, onCategoryReorder]
  )

  if (mode === 'categories') {
    if (!isReview) {
      return (
        <Paper sx={{ p: 3, width: '100%', maxWidth }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Approved categories
            </Typography>
            <Typography variant="body2" color="text.secondary">
              These categories are approved and queued for rewards preparation.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category name</TableCell>
                    <TableCell align="center">Display order</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedCategories.map((category) => {
                    const categoryId = category.id || category.tempId || ''

                    return (
                      <TableRow key={categoryId} hover>
                        <TableCell>
                          <Typography variant="body2">{category.name}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="text.secondary">
                            {category.displayOrder}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Paper>
      )
    }

    return (
      <Paper sx={{ p: 3, width: '100%', maxWidth }}>
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Review categories
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI has prepared {categories.length} categories. Drag to reorder, update names, or remove any outliers.
          </Typography>
          <TableContainer>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category name</TableCell>
                    <TableCell align="center">Order</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <SortableContext
                  items={sortedCategories.map((category) => category.id || category.tempId || '')}
                  strategy={verticalListSortingStrategy}
                >
                  <TableBody>
                    {sortedCategories.map((category) => {
                      const categoryId = category.id || category.tempId || ''
                      const isDraggable = category.displayOrder !== 0

                      return (
                        <SortableCategoryRow
                          key={categoryId}
                          category={category}
                          onCategoryEdit={onCategoryEdit}
                          onCategoryRemove={onCategoryRemove}
                          isDraggable={isDraggable}
                        />
                      )
                    })}
                  </TableBody>
                </SortableContext>
              </Table>
            </DndContext>
          </TableContainer>
          {footer && <Box>{footer}</Box>}
        </Stack>
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 3, width: '100%', maxWidth }}>
      <Stack spacing={2}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {isReview ? 'Review rewards' : 'Approved rewards'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isReview
            ? `AI has prepared ${rewards.length} rewards. Confirm categories, adjust names, and review contributions.`
            : `These rewards are approved and queued for the next step.`}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reward name</TableCell>
                <TableCell align="center">Order</TableCell>
                <TableCell align="center">Employee contribution</TableCell>
                {isReview && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rewardGroups.map((group) => (
                <Fragment key={group.id}>
                  <TableRow>
                    <TableCell colSpan={isReview ? 4 : 3}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {group.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {group.rewards.length} reward{group.rewards.length === 1 ? '' : 's'}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={
                      isReview
                        ? (event) => {
                            const { active, over } = event
                            if (!over || active.id === over.id) return

                            const oldIndex = group.rewards.findIndex(
                              (reward) => (reward.id || reward.tempId) === active.id
                            )
                            const newIndex = group.rewards.findIndex(
                              (reward) => (reward.id || reward.tempId) === over.id
                            )
                            if (oldIndex === -1 || newIndex === -1) return

                            const reordered = arrayMove(group.rewards, oldIndex, newIndex)
                            const updatedById = new Map<string, RewardData>()
                            reordered.forEach((reward, index) => {
                              const rewardId = reward.id || reward.tempId || ''
                              updatedById.set(rewardId, { ...reward, displayOrder: index + 1 })
                            })

                            const nextRewards = rewards.map((reward) => {
                              const rewardId = reward.id || reward.tempId || ''
                              const updated = updatedById.get(rewardId)
                              return updated ? updated : reward
                            })

                            onRewardsReorder(nextRewards)
                          }
                        : undefined
                    }
                  >
                    <SortableContext
                      items={group.rewards.map((reward) => reward.id || reward.tempId || '')}
                      strategy={verticalListSortingStrategy}
                    >
                      {group.rewards.map((reward) => (
                        <SortableRewardRow
                          key={reward.id || reward.tempId || ''}
                          reward={reward}
                          onRewardEdit={onRewardEdit}
                          onRewardRemove={onRewardRemove}
                          isDraggable={isReview}
                          variant={variant}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {footer && <Box>{footer}</Box>}
      </Stack>
    </Paper>
  )
}

export default ReviewTable
